import type { AppContext } from '@application/context/AppContext';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { isHybridDeployment } from '@infra/licence/DataModeResolver';
import { LicensingSessionService } from '@infra/licence/LicensingSessionService';
import { SqliteLocalRepositoriesAdapter } from '@infra/persistence/adapters/SqliteLocalRepositoriesAdapter';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import type { User } from '@shared/types/User';
import { HybridAuthService, type HybridLoginMode, type HybridUserDirectory } from './HybridAuthService';

/** Licence signée « hybride » : les comptes sont ceux du serveur. */
export function isHybridAccountsMode(ctx: AppContext): boolean {
  return isHybridDeployment(ctx.getLicenceRuntime?.()?.getState()?.claims ?? null);
}

/** Annuaire local des comptes (SQLite chiffré, ou stockage de secours) servant de cache d'identité. */
function createLocalDirectory(ctx: AppContext): HybridUserDirectory {
  return {
    findByEmail: async (email, tenantId) => {
      const rows = await ctx.localTables.list<User>('users', tenantId);
      return rows.find((u) => u.email === email && (!tenantId || u.tenant_id === tenantId));
    },
    findById: async (id) => (await ctx.localTables.get<User>('users', id)) ?? null,
    save: async (user) => {
      await ctx.localTables.put('users', user);
    },
    remove: async (id) => {
      if (await SqliteLocalRepositoriesAdapter.isActive()) await SqliteEntityBridge.delete('users', id);
      else await ctx.db.users.delete(id);
    },
  };
}

export function createHybridAuth(ctx: AppContext): HybridAuthService {
  return new HybridAuthService({
    api: getBusinessApiClient(),
    users: createLocalDirectory(ctx),
    persistSession: (session) => ctx.auth.restoreSession(session),
    ensureInstallation: (tenantId) => LicensingSessionService.ensureInstallationSession(tenantId),
    provisionLegacy: async (user, password) => {
      // Seul le premier administrateur d'un serveur encore vide peut être migré automatiquement ;
      // les autres comptes sont créés sur le serveur par un administrateur (écran Utilisateurs).
      if (user.role !== 'admin' && user.role !== 'super_admin') return false;
      const api = getBusinessApiClient();
      const status = await api.getBootstrapStatus();
      if (!status.needs_first_admin) return false;
      await api.registerFirstAdmin({ email: user.email, password, nom: user.nom, prenom: user.prenom, role: 'admin' });
      return true;
    },
  });
}

/** Ouverture de session Hybride : serveur d'abord, identité mémorisée si le serveur est injoignable. */
export async function performHybridLogin(
  ctx: AppContext,
  input: { email: string; password: string; tenantId?: string },
): Promise<HybridLoginMode> {
  const { mode } = await createHybridAuth(ctx).login(input);
  return mode;
}

/** Rôle renvoyé par le serveur pendant une session en ligne : le cache d'identité le suit. */
export async function applyServerSessionToIdentityCache(
  ctx: AppContext,
  serverSession: { user_id: string; role: string },
): Promise<void> {
  try {
    await createHybridAuth(ctx).applyServerSession(serverSession);
    await ctx.auth.refreshSessionFromDb();
  } catch {
    /* le cache d'identité est un confort : jamais bloquant */
  }
}
